// Portions of this code are adapted from the MUI Toolbar component.
// Source: https://github.com/mui/toolpad/blob/master/packages/toolpad-core/src/DashboardLayout/DashboardLayout.tsx
// MUI is licensed under the MIT License: https://github.com/mui/toolpad/blob/master/LICENSE
import { useCallback } from 'react';

import Drawer from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';

import { MINI_DRAWER_WIDTH } from '../shared';
import { Navigation } from '../types';
import { SidebarContent } from './components/SidebarContent';
import { useSidebarContext } from './context/SidebarContext';
import { getDrawerWidthTransitionMixin } from './utils';

interface SidebarProps {
  navigation: Navigation;

  /**
   * Whether the sidebar should not be collapsible to a mini variant in desktop and tablet viewports.
   * @default false
   */
  disableCollapsibleSidebar?: boolean;

  /**
   * Width of the sidebar when expanded.
   * @default 320
   */
  sidebarExpandedWidth?: number | string;
}

function Sidebar({ navigation, disableCollapsibleSidebar = false, sidebarExpandedWidth = 320 }: SidebarProps) {
  const theme = useTheme();

  const { isDesktopNavigationExpanded, isMobileNavigationExpanded, isNavigationExpanded, setIsNavigationExpanded } =
    useSidebarContext();

  const isOverSmViewport = useMediaQuery(theme.breakpoints.up('sm'));
  const isOverMdViewport = useMediaQuery(theme.breakpoints.up('md'));

  const isDesktopMini = !disableCollapsibleSidebar && !isDesktopNavigationExpanded;
  const isMobileMini = !disableCollapsibleSidebar && !isMobileNavigationExpanded;

  const hasDrawerTransitions = isOverSmViewport && (!disableCollapsibleSidebar || isOverMdViewport);

  const getDrawerContent = useCallback(
    (isMini: boolean) => {
      return (
        <>
          <Toolbar />
          <SidebarContent
            navigation={navigation}
            isMini={isMini}
            hasDrawerTransitions={hasDrawerTransitions}
            sidebarExpandedWidth={sidebarExpandedWidth}
          />
        </>
      );
    },
    [hasDrawerTransitions, navigation, sidebarExpandedWidth],
  );

  const getDrawerSharedSx = useCallback(
    (isMini: boolean, isTemporary: boolean) => {
      const drawerWidth = isMini ? MINI_DRAWER_WIDTH : sidebarExpandedWidth;

      return {
        displayPrint: 'none',
        width: drawerWidth,
        flexShrink: 0,
        ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        ...(isTemporary ? { position: 'absolute' } : {}),
        [`& .MuiDrawer-paper`]: {
          position: 'absolute',
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundImage: 'none',
          ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        },
      };
    },
    [isNavigationExpanded, sidebarExpandedWidth],
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={isMobileNavigationExpanded}
        onClose={() => setIsNavigationExpanded(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: {
            xs: 'block',
            sm: disableCollapsibleSidebar ? 'block' : 'none',
            md: 'none',
          },
          ...getDrawerSharedSx(false, true),
        }}
      >
        {getDrawerContent(false)}
      </Drawer>

      {/* Tablet drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: {
            xs: 'none',
            sm: disableCollapsibleSidebar ? 'none' : 'block',
            md: 'none',
          },
          ...getDrawerSharedSx(isMobileMini, false),
        }}
      >
        {getDrawerContent(isMobileMini)}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          ...getDrawerSharedSx(isDesktopMini, false),
        }}
      >
        {getDrawerContent(isDesktopMini)}
      </Drawer>
    </>
  );
}

export { Sidebar };
